import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ListChecks, Clock, Send, Bell, CheckCircle, XCircle,
  ArrowRight, Pause, RefreshCw, Filter
} from 'lucide-react';

export default function DocsApprovalQueue() {
  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 mb-4">
          Approval Queue
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Slack Approval Queue
        </h1>
        <p className="text-lg text-[#94A3B8] mb-6">
          Decisions waiting for approval. Re-request, snooze, and keep operations moving.
        </p>
        <div className="flex gap-4">
          <Link to="/dashboard/approvals">
            <Button className="bg-purple-500 text-white hover:bg-purple-600" data-testid="go-to-approval-queue">
              Open Approval Queue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>

      {/* What is Approval Queue */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          What is the Approval Queue?
        </h2>
        <div className="bg-[#0D1628] border border-[#22304A] rounded-lg p-6">
          <p className="text-[#94A3B8] mb-4">
            The Approval Queue shows all decision traces awaiting approval in Slack. 
            It's your central hub for managing pending exceptions and keeping the team aligned.
          </p>
          <ul className="space-y-2 text-[#94A3B8]">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              View all pending approvals in one place
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              Bulk select and re-request stale items
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              Snooze items temporarily to reduce noise
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              Auto-reminders post as a digest to avoid channel spam
            </li>
          </ul>
        </div>
      </section>

      {/* Key Actions */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Key Actions
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <Send className="w-6 h-6 text-sky-400 mb-2" />
              <CardTitle className="text-white">Re-request Approval</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Send selected approvals back to Slack. We rate-limit at 15/min to avoid Slack spam.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <Pause className="w-6 h-6 text-amber-400 mb-2" />
              <CardTitle className="text-white">Snooze</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Hide this approval temporarily. Snoozed items won't appear in the queue until they return.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <RefreshCw className="w-6 h-6 text-emerald-400 mb-2" />
              <CardTitle className="text-white">Un-snooze</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Bring snoozed items back to the active queue before their scheduled return time.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <Filter className="w-6 h-6 text-purple-400 mb-2" />
              <CardTitle className="text-white">Filter & Sort</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Filter by type, severity, or age. Sort by oldest first to tackle the backlog.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Bulk Actions */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Bulk Actions
        </h2>
        <div className="bg-[#0D1628] border border-[#22304A] rounded-lg p-6">
          <p className="text-[#94A3B8] mb-4">
            Select multiple items using the checkboxes, then use the bulk action bar:
          </p>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-[#1E293B] rounded-lg">
              <ListChecks className="w-5 h-5 text-sky-400" />
              <div>
                <span className="text-white font-medium">Select All / None</span>
                <p className="text-xs text-[#94A3B8]">Quickly toggle selection for all visible items</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-[#1E293B] rounded-lg">
              <Send className="w-5 h-5 text-emerald-400" />
              <div>
                <span className="text-white font-medium">Re-request Selected</span>
                <p className="text-xs text-[#94A3B8]">Posts are sent at 15/min to respect Slack rate limits</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-[#1E293B] rounded-lg">
              <Pause className="w-5 h-5 text-amber-400" />
              <div>
                <span className="text-white font-medium">Snooze Selected</span>
                <p className="text-xs text-[#94A3B8]">Hide items for 24h, 3d, or 7d</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Auto-Reminders */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Auto-Reminders
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Bell className="w-8 h-8 text-amber-400 flex-shrink-0" />
              <div>
                <h3 className="text-white font-semibold mb-2">Digest-Style Reminders</h3>
                <p className="text-[#94A3B8] mb-4">
                  Auto-reminders consolidate all pending approvals for a workspace into a single digest message. 
                  This avoids channel noise while keeping the team aware of outstanding decisions.
                </p>
                <div className="p-3 bg-[#1E293B] rounded-lg">
                  <p className="text-xs text-[#64748B]">
                    <strong>Reminder Schedule:</strong> Configurable via Slack Settings. 
                    Reminders only fire if there are pending, non-snoozed items.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Status Guide */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Approval Status Guide
        </h2>
        <div className="space-y-3">
          <div className="flex items-center gap-4 p-4 bg-[#0D1628] rounded-lg border border-[#22304A]">
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 min-w-[100px] justify-center">Pending</Badge>
            <p className="text-sm text-[#94A3B8]">Awaiting decision in Slack</p>
          </div>
          <div className="flex items-center gap-4 p-4 bg-[#0D1628] rounded-lg border border-[#22304A]">
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 min-w-[100px] justify-center">Approved</Badge>
            <p className="text-sm text-[#94A3B8]">Decision approved via Slack or UI</p>
          </div>
          <div className="flex items-center gap-4 p-4 bg-[#0D1628] rounded-lg border border-[#22304A]">
            <Badge className="bg-rose-500/20 text-rose-400 border-rose-500/30 min-w-[100px] justify-center">Rejected</Badge>
            <p className="text-sm text-[#94A3B8]">Decision rejected via Slack or UI</p>
          </div>
          <div className="flex items-center gap-4 p-4 bg-[#0D1628] rounded-lg border border-[#22304A]">
            <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30 min-w-[100px] justify-center">Snoozed</Badge>
            <p className="text-sm text-[#94A3B8]">Temporarily hidden, will return automatically</p>
          </div>
        </div>
      </section>

      {/* Plan Availability */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Plan Availability
        </h2>
        <div className="bg-gradient-to-r from-emerald-900/30 to-emerald-800/20 border border-emerald-500/30 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Pro</Badge>
            <span className="text-white font-semibold">Decision Graph + Slack — $299/mo</span>
          </div>
          <p className="text-[#94A3B8]">
            The Approval Queue is part of the Pro plan, which includes Slack approvals, 
            bulk operations, snooze functionality, and auto-reminders.
          </p>
        </div>
      </section>

      {/* Next Steps */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6">
          Next Steps
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/docs/slack-integration">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-purple-500/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Slack Integration →</h3>
                <p className="text-sm text-[#94A3B8]">Connect Slack and set default channels</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/promise-health">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-purple-500/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Promise Health →</h3>
                <p className="text-sm text-[#94A3B8]">Monitor your promise-keeping performance</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
